import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Briefcase, 
  MapPin, 
  Calendar, 
  User, 
  Phone, 
  Mail,
  Clock,
  FileText,
  Receipt,
  Sparkles,
  ClipboardList,
  AlertTriangle,
  Plus,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
  Lock,
  Tag,
  GanttChartSquare,
  Package
} from 'lucide-react';
import GlassPanel from '../components/GlassPanel';
import EmptyState from '../components/EmptyState';
import { AIPanel, AIButton, AIExplanationBox, AIBadge } from '../components/AIComponents';
import { 
  LineageBanner, 
  ImmutableNotice, 
  VariationBadge,
  CreateInvoiceFromJobModal,
  ProposeVariationModal 
} from '../components/LineageComponents';
import { JobMarginCard } from '../components/MarginComponents';
import { MaterialsPlanningPanel, PurchaseOrdersList, CommittedCostsWidget } from '../components/MaterialsComponents';
import { GanttChart } from '../components/SchedulingComponents';
import { JobLocationSection } from '../components/MapsComponents';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  'in-progress': 'active',
  'in_progress': 'active',
  'on-site': 'active',
  'on_site': 'active',
  'scheduled': 'pending',
  'booked': 'pending',
  'completed': 'completed',
  'paid': 'completed',
  'quoted': 'draft',
  'enquiry': 'draft',
  'quote-sent': 'pending',
  'quote_sent': 'pending',
  'quote-accepted': 'completed',
  'quote_accepted': 'completed'
};

const lineItemStatusColors = {
  'PENDING': 'draft',
  'IN_PROGRESS': 'active',
  'COMPLETED': 'completed',
  'DEFERRED': 'pending',
  'CANCELLED': 'overdue'
};

export function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, orgId } = useAuth();
  
  const [job, setJob] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [variations, setVariations] = useState([]);
  const [lineageInfo, setLineageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showVariationModal, setShowVariationModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [updatingItem, setUpdatingItem] = useState(null);

  // Fetch job data
  const fetchJobData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch job details
      const jobRes = await fetch(`${API_URL}/api/jobs/${id}?org_id=${orgId}`, {
        headers: getAuthHeaders()
      });
      
      if (!jobRes.ok) throw new Error('Failed to fetch job');
      const jobData = await jobRes.json();
      setJob(jobData);

      // Fetch line items if job has lineage
      if (jobData.source_quote_id) {
        const [itemsRes, lineageRes, variationsRes] = await Promise.all([
          fetch(`${API_URL}/api/lineage/jobs/${id}/line-items`, { headers: getAuthHeaders() }),
          fetch(`${API_URL}/api/lineage/verify/job/${id}`, { headers: getAuthHeaders() }),
          fetch(`${API_URL}/api/lineage/jobs/${id}/variations`, { headers: getAuthHeaders() })
        ]);

        if (itemsRes.ok) {
          const items = await itemsRes.json();
          setLineItems(Array.isArray(items) ? items : []);
        }

        if (lineageRes.ok) {
          setLineageInfo(await lineageRes.json());
        }

        if (variationsRes.ok) {
          const vars = await variationsRes.json();
          setVariations(Array.isArray(vars) ? vars : []);
        }
      }
    } catch (err) {
      console.error('Error fetching job:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, orgId, getAuthHeaders]);

  useEffect(() => {
    if (id && orgId) {
      fetchJobData();
    }
  }, [id, orgId, fetchJobData]);

  // Update line item status
  const updateLineItemStatus = async (itemId, newStatus) => {
    setUpdatingItem(itemId);
    try {
      const res = await fetch(`${API_URL}/api/lineage/jobs/line-items/${itemId}/status`, {
        method: 'PATCH',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!res.ok) throw new Error('Failed to update status');

      // Refresh line items
      const itemsRes = await fetch(`${API_URL}/api/lineage/jobs/${id}/line-items`, { headers: getAuthHeaders() });
      if (itemsRes.ok) {
        const items = await itemsRes.json();
        setLineItems(Array.isArray(items) ? items : []);
      }

      setSuccessMessage(`Item marked as ${newStatus}`);
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err) {
      console.error('Error updating status:', err);
      setError(err.message);
    } finally {
      setUpdatingItem(null);
    }
  };

  // Approve variation
  const approveVariation = async (variationId) => {
    try {
      const res = await fetch(`${API_URL}/api/lineage/variations/${variationId}/approve`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!res.ok) throw new Error('Failed to approve variation');

      setSuccessMessage('Variation approved and added to job');
      fetchJobData(); // Refresh all data
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err) {
      console.error('Error approving variation:', err);
      setError(err.message);
    }
  };

  const handleInvoiceCreated = (result) => {
    setSuccessMessage(`Invoice ${result.invoice_number} created!`);
    setTimeout(() => {
      navigate(`/invoices/${result.invoice_id}`);
    }, 1500);
  };

  const handleVariationCreated = (result) => {
    setSuccessMessage('Variation proposed successfully');
    fetchJobData();
    setTimeout(() => setSuccessMessage(null), 2000);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-3xl)' }}>
        <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent-primary)' }} />
      </div>
    );
  }

  if (error && !job) {
    return (
      <div className="page">
        <Link to="/jobs" className="back-link">
          <ArrowLeft size={16} />Back to Jobs
        </Link>
        <div style={{
          padding: 'var(--space-lg)',
          background: 'var(--status-danger-bg)',
          borderRadius: 'var(--radius-md)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)'
        }}>
          <AlertCircle size={18} style={{ color: 'var(--status-danger)' }} />
          <span style={{ color: 'var(--status-danger)' }}>{error}</span>
        </div>
      </div>
    );
  }

  const hasLineage = job?.source_quote_id;
  const completedItems = lineItems.filter(i => i.status === 'COMPLETED' && !i.invoiced);
  const canCreateInvoice = hasLineage && completedItems.length > 0;
  const pendingVariations = variations.filter(v => v.status === 'PROPOSED');
  const totalValue = lineItems.reduce((sum, i) => sum + (i.line_total_net || 0), 0);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 'var(--space-xl)', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Main Content */}
      <div className="detail-page" style={{ maxWidth: 'none' }}>
        {/* Back Link */}
        <Link to="/jobs" className="back-link">
          <ArrowLeft size={16} />
          Back to Jobs
        </Link>

        {/* Success Message */}
        {successMessage && (
          <div style={{
            padding: 'var(--space-md)',
            background: 'var(--status-success-bg)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-lg)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)'
          }}>
            <CheckCircle size={18} style={{ color: 'var(--status-success)' }} />
            <span style={{ color: 'var(--status-success)' }}>{successMessage}</span>
          </div>
        )}

        {/* Lineage Banner */}
        {hasLineage && (
          <LineageBanner 
            quoteId={job.source_quote_id}
            jobId={job.id}
            jobTitle={job.title}
            isImmutable={true}
          />
        )}

        {/* No Lineage Warning */}
        {!hasLineage && (
          <div style={{
            background: 'var(--status-warning-bg)',
            border: '1px solid var(--status-warning)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-md)',
            marginBottom: 'var(--space-lg)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--space-sm)'
          }}>
            <AlertTriangle size={18} style={{ color: 'var(--status-warning)', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <div style={{ fontWeight: '600', color: 'var(--status-warning)', marginBottom: '4px' }}>
                No Quote Lineage
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                This job was not created from an accepted quote. Invoices cannot be created without Quote → Job → Invoice lineage.
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="detail-header">
          <div className="detail-title-row">
            <div>
              <h1 className="detail-title" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                {job?.title || 'Untitled Job'}
                <AIBadge text="AI" />
              </h1>
              <p className="detail-subtitle">{job?.client_name || 'Client'} • Job #{id?.slice(-4)}</p>
            </div>
            <span className={`status-badge ${statusColors[job?.status?.toLowerCase()] || 'draft'}`} style={{ padding: '8px 16px', fontSize: '0.75rem' }}>
              {job?.status?.replace('_', ' ') || 'Unknown'}
            </span>
          </div>
        </div>

        {/* Main Info */}
        <div className="detail-grid mb-xl">
          <div className="detail-item">
            <div className="detail-item-label">Location</div>
            <div className="detail-item-value" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <MapPin size={16} style={{ color: 'var(--accent-primary)' }} />
              {job?.address || 'No address'}
            </div>
          </div>
          <div className="detail-item">
            <div className="detail-item-label">Schedule</div>
            <div className="detail-item-value" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <Calendar size={16} style={{ color: 'var(--accent-primary)' }} />
              {job?.target_start_date ? new Date(job.target_start_date).toLocaleDateString('en-GB') : 'Not scheduled'}
            </div>
          </div>
          <div className="detail-item">
            <div className="detail-item-label">Job Value</div>
            <div className="detail-item-value" style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--status-success)' }}>
              £{(totalValue || job?.quote_amount_gross || 0).toFixed(2)}
            </div>
          </div>
          <div className="detail-item">
            <div className="detail-item-label">Source Quote</div>
            <div className="detail-item-value" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              {hasLineage ? (
                <>
                  <Lock size={16} style={{ color: 'var(--status-warning)' }} />
                  <Link to={`/quotes/${job.source_quote_id}`} style={{ color: 'var(--accent-primary)' }}>
                    #{job.source_quote_id?.slice(-4)}
                  </Link>
                </>
              ) : (
                <span style={{ color: 'var(--text-muted)' }}>None</span>
              )}
            </div>
          </div>
        </div>

        {/* Location & Distance Section */}
        {job?.id && orgId && (
          <div className="mb-xl">
            <JobLocationSection 
              jobId={job.id}
              orgId={orgId}
              getAuthHeaders={getAuthHeaders}
              userRole="MANAGER" // TODO: Get actual role from context
            />
          </div>
        )}

        {/* Line Items */}
        {hasLineage && lineItems.length > 0 && (
          <GlassPanel 
            title="Line Items" 
            titleIcon={ClipboardList} 
            className="mb-xl"
            headerAction={
              <button 
                onClick={() => setShowVariationModal(true)}
                className="btn btn-secondary"
                style={{ fontSize: '0.75rem', padding: '6px 12px' }}
                data-testid="propose-variation-btn"
              >
                <Plus size={14} />
                Propose Variation
              </button>
            }
          >
            <ImmutableNotice entityType="line item values" />
            
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                    <th style={{ textAlign: 'left', padding: 'var(--space-md)', fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Description</th>
                    <th style={{ textAlign: 'center', padding: 'var(--space-md)', fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Status</th>
                    <th style={{ textAlign: 'right', padding: 'var(--space-md)', fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Qty</th>
                    <th style={{ textAlign: 'right', padding: 'var(--space-md)', fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Total</th>
                    <th style={{ textAlign: 'center', padding: 'var(--space-md)', fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((item) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                      <td style={{ padding: 'var(--space-md)', color: 'var(--text-primary)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                          {item.description}
                          {item.is_variation && <VariationBadge reason={item.variation_reason} />}
                          {item.invoiced && (
                            <span style={{ 
                              fontSize: '0.6875rem', 
                              color: 'var(--status-success)',
                              background: 'var(--status-success-bg)',
                              padding: '2px 6px',
                              borderRadius: 'var(--radius-sm)'
                            }}>
                              Invoiced
                            </span>
                          )}
                        </div>
                        {item.source_quote_line_id && (
                          <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                            From Quote Line #{item.source_quote_line_id?.slice(-4)}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
                        <span className={`status-badge ${lineItemStatusColors[item.status] || 'draft'}`}>
                          {item.status?.replace('_', ' ')}
                        </span>
                      </td>
                      <td style={{ padding: 'var(--space-md)', textAlign: 'right', color: 'var(--text-secondary)' }}>
                        {item.quantity}
                      </td>
                      <td style={{ padding: 'var(--space-md)', textAlign: 'right', color: 'var(--text-primary)', fontWeight: '500' }}>
                        £{(item.line_total_net || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
                        {!item.invoiced && item.status !== 'COMPLETED' && item.status !== 'CANCELLED' && (
                          <button
                            onClick={() => updateLineItemStatus(item.id, 'COMPLETED')}
                            disabled={updatingItem === item.id}
                            className="btn btn-secondary"
                            style={{ fontSize: '0.6875rem', padding: '4px 8px' }}
                            data-testid={`complete-item-${item.id}`}
                          >
                            {updatingItem === item.id ? (
                              <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                            ) : (
                              <CheckCircle size={12} />
                            )}
                            Complete
                          </button>
                        )}
                        {!item.invoiced && item.status === 'PENDING' && (
                          <button
                            onClick={() => updateLineItemStatus(item.id, 'DEFERRED')}
                            disabled={updatingItem === item.id}
                            className="btn btn-secondary"
                            style={{ fontSize: '0.6875rem', padding: '4px 8px', marginLeft: '4px' }}
                          >
                            Defer
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid var(--glass-border)', background: 'var(--glass-bg)' }}>
                    <td colSpan="3" style={{ padding: 'var(--space-md)', textAlign: 'right', fontWeight: '600', color: 'var(--text-primary)' }}>
                      Total:
                    </td>
                    <td style={{ padding: 'var(--space-md)', textAlign: 'right', fontWeight: '700', color: 'var(--accent-primary)', fontSize: '1.125rem' }}>
                      £{totalValue.toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </GlassPanel>
        )}

        {/* Pending Variations */}
        {pendingVariations.length > 0 && (
          <GlassPanel 
            title="Pending Variations" 
            titleIcon={Tag} 
            className="mb-xl"
            style={{ background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(245, 158, 11, 0.1) 100%)' }}
          >
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 'var(--space-md)' }}>
              These variations require approval before they become job line items.
            </p>
            {pendingVariations.map((variation) => (
              <div 
                key={variation.id}
                style={{
                  padding: 'var(--space-md)',
                  background: 'var(--glass-bg)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 'var(--space-sm)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
                data-testid={`variation-${variation.id}`}
              >
                <div>
                  <div style={{ fontWeight: '500', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                    {variation.description}
                    <VariationBadge reason={variation.reason} />
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                    Reason: {variation.reason}
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--status-warning)', marginTop: '4px' }}>
                    £{(variation.line_total_net || 0).toFixed(2)}
                  </div>
                </div>
                <button
                  onClick={() => approveVariation(variation.id)}
                  className="btn btn-primary"
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                  data-testid={`approve-variation-${variation.id}`}
                >
                  <CheckCircle size={14} />
                  Approve
                </button>
              </div>
            ))}
          </GlassPanel>
        )}

        {/* Description */}
        {job?.notes && (
          <GlassPanel title="Notes" titleIcon={Briefcase} className="mb-xl">
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              {job.notes}
            </p>
          </GlassPanel>
        )}

        {/* Margin Analysis */}
        {job?.id && orgId && (
          <div className="mb-xl">
            <JobMarginCard jobId={job.id} orgId={orgId} />
          </div>
        )}

        {/* Committed Costs Widget */}
        {job?.id && orgId && (
          <div className="mb-xl">
            <CommittedCostsWidget jobId={job.id} orgId={orgId} />
          </div>
        )}

        {/* Materials Planning */}
        {job?.id && orgId && (
          <div className="mb-xl">
            <MaterialsPlanningPanel jobId={job.id} orgId={orgId} />
          </div>
        )}

        {/* Purchase Orders */}
        {job?.id && orgId && (
          <div className="mb-xl">
            <PurchaseOrdersList jobId={job.id} orgId={orgId} />
          </div>
        )}

        {/* Timeline / Gantt */}
        {job?.id && orgId && (
          <GlassPanel 
            title="Timeline & Schedule" 
            titleIcon={GanttChartSquare}
            className="mb-xl"
          >
            <GanttChart jobId={job.id} orgId={orgId} />
          </GlassPanel>
        )}

        {/* Create Invoice CTA */}
        {canCreateInvoice && (
          <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
            <button 
              onClick={() => setShowInvoiceModal(true)}
              className="btn btn-primary" 
              style={{ flex: 1 }}
              data-testid="create-invoice-btn"
            >
              <Receipt size={16} />
              Create Invoice ({completedItems.length} items ready)
            </button>
          </div>
        )}
      </div>

      {/* AI Side Panel */}
      <div className="ai-side-panel">
        <AIPanel 
          title="AI Job Assistant" 
          description="AI helps summarise and plan. It never changes job data automatically."
        >
          <div className="ai-actions-grid single-col">
            <AIButton icon={Sparkles} variant="full" tooltip="AI generation will be enabled soon">
              Summarise job status (AI)
            </AIButton>
            <AIButton icon={ClipboardList} variant="full" tooltip="AI generation will be enabled soon">
              Create weekly action list
            </AIButton>
            <AIButton icon={AlertTriangle} variant="full" tooltip="AI generation will be enabled soon">
              Predict delays
            </AIButton>
          </div>
        </AIPanel>

        <AIExplanationBox>
          AI analyses job progress and timeline to provide insights. All values are inherited from the source quote and cannot be modified.
        </AIExplanationBox>
      </div>

      {/* Modals */}
      <CreateInvoiceFromJobModal
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        job={job}
        onSuccess={handleInvoiceCreated}
      />

      <ProposeVariationModal
        isOpen={showVariationModal}
        onClose={() => setShowVariationModal(false)}
        job={job}
        onSuccess={handleVariationCreated}
      />
    </div>
  );
}

export default JobDetail;
